import type { ReactElement } from "react";

import type { IconName } from "../../components/ui/icon";
import type { DataTableGroupSortDirection } from "../../hooks/use-data-table-state";
import type { Omittable } from "../../lib/props";
import {
  FieldDirectionDropdown,
  type FieldDropdownCopy,
} from "./data-table-field-dropdown";

export type DataTableSortByDropdownProps = Omittable<{
  /** The active sort field key, or "none" while unsorted. */
  sortByField: string;
  setSortByField: (field: string) => void;
  sortByLabels: Record<string, string>;
  sortByIcons?: Partial<Record<string, IconName>>;
  sortDirection?: DataTableGroupSortDirection;
  setSortDirection?: (direction: DataTableGroupSortDirection) => void;
  /** Caller-owned trigger element replacing the default toolbar button — a
   * settings row that presses across its whole width, say. It receives the
   * trigger's own onClick / aria-expanded / data-popup-open / ref, so it has to
   * forward unknown props to the element it renders. A trigger that already
   * names the active field carries no tooltip: the tooltip exists to answer
   * "sorted by what?" for the compact button face. */
  trigger?: ReactElement<Record<string, unknown>>;
  /** Suppress the active-field label on the trigger and show only the icon —
   * the same collapsed look the toggle takes on at narrow/mobile widths —
   * even when there's room for the label. Use where the toolbar should stay
   * compact (e.g. a narrow drawer). */
  iconOnly?: boolean;
}>;

const COPY: FieldDropdownCopy = {
  title: "Sort by",
  tooltipVerb: "Sorting by",
  directionLabel: "Sort order",
  icon: "arrow-up-down",
};

/** The group-by dropdown's sibling: pick the field rows ORDER BY, and its
 *  direction. A host can always render unsorted — its rows fall back to their
 *  natural order — so the "—" clear row always heads the list. */
export function DataTableSortByDropdown({
  sortByField,
  setSortByField,
  sortByLabels,
  sortByIcons,
  sortDirection,
  setSortDirection,
  trigger,
  iconOnly,
}: DataTableSortByDropdownProps) {
  return (
    <FieldDirectionDropdown
      field={sortByField}
      setField={setSortByField}
      labels={sortByLabels}
      icons={sortByIcons}
      direction={sortDirection}
      setDirection={setSortDirection}
      trigger={trigger}
      iconOnly={iconOnly}
      copy={COPY}
    />
  );
}
